import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import "./equipmentPieChart.scss";
import {FormatLocaleNumber} from "../DashboardUtils";

export interface EquipmentPieChartRow {
    label: string;
    frequency: number;
    color: string;
    id: string;
}

export interface EquipmentPieChartProps {
    radius: number;
    data: EquipmentPieChartRow[];
    selectedItem: string;
    onPathHover: Function;
}

interface EquipmentPieChartState {
    selectedItem: string;
}

export class EquipmentPieChart extends React.Component<EquipmentPieChartProps, EquipmentPieChartState> {

    private static DEFAULT_COLOR: string = "var(--vebox-gray-hover-light)";

    constructor(props: EquipmentPieChartProps) {
        super(props);

        this.state = {
            selectedItem: undefined,
        };

        this.mouseOut = this.mouseOut.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
    }

    public render() {
        const div = new ReactFauxDOM.Element("div");
        const data: EquipmentPieChartRow[] = this.props.data;
        const radius = this.props.radius;
        const pieDimensions = {
            radius: this.props.radius,
            width: this.props.radius * 2,
        };
        pieDimensions.radius = Math.min(pieDimensions.width, pieDimensions.width) / 2;

        const svg = d3.select(div).append("svg")
            .attr("width", pieDimensions.width + 50)
            .attr("height", pieDimensions.width + 50);

        const pieSvg = svg.append("g")
            .attr(
                "transform",
                "translate(" + (pieDimensions.width + 50) / 2 + "," + (pieDimensions.width + 50) / 2 + ")",
            );
        const filterId = "dropshadow" + Math.random() * 100000;
        const filter = pieSvg.append("filter")
            .attr("id", filterId);

        filter.append("feGaussianBlur")
            .attr("in", "SourceAlpha")
            .attr("stdDeviation", 4)
            .attr("result", "blur");
        filter.append("feOffset")
            .attr("in", "blur")
            .attr("dx", 2)
            .attr("dy", 2)
            .attr("result", "offsetBlur");
        filter.append("feFlood")
            .attr("in", "offsetBlur")
            .attr("flood-color", "#3d3d3d")
            .attr("flood-opacity", "0.5")
            .attr("result", "offsetColor");
        filter.append("feComposite")
            .attr("in", "offsetColor")
            .attr("in2", "offsetBlur")
            .attr("operator", "in")
            .attr("result", "offsetBlur");

        // pieSvg.style("filter", "url(#" + filterId + ")");

        const feMerge = filter.append("feMerge");

        feMerge.append("feMergeNode")
            .attr("in", "offsetBlur");
        feMerge.append("feMergeNode")
            .attr("in", "SourceGraphic");

        const outerRadius = pieDimensions.radius - 10,
            innerRadius = 0;
        const arc = d3.arc().outerRadius(outerRadius).innerRadius(innerRadius);
        const pie = d3.pie().value((d) => (d as any).frequency);
        pieSvg.selectAll("path")
            .data(pie(data as any))
            .enter()
            .append("path")
            .style("opacity", ".8")
            .attr("data-id", function (d) {
                return (d as any).data.label;
            })
            .attr("d", arc as any)
            .style("fill", (d) => {
                return (d as any).data.color;
            })
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut)
            .on("click", (event, data) => {
                const pieRow = (document.querySelectorAll(`[data-equipment-id='${data.index}']`)[0] as HTMLDivElement);
                pieRow.click();
            });

        function midAngle(d) {
            return d.startAngle + (d.endAngle - d.startAngle) / 2;
        }

        const dataSet = this.props.data;
        const total = dataSet.map((item) => {
            return item.frequency;
        }).reduce((a, b) => {
            return a + b;
        }, 0);
        const getPercentage: Function = (total: number, value: number): number => {
            return 100 * value / total;
        };
        pieSvg.append("g")
            .attr("class", "pie-chart__labels");
        pieSvg.append("g")
            .attr("class", "lines");
        const text = svg.select(".pie-chart__labels").selectAll("text")
            .data(pie(data as any));
        text.enter()
            .append("text")
            .attr("dy", ".35em")
            .text((d) => {
                return FormatLocaleNumber((d as any).data.frequency);
            })
            .attr("transform", function (d) {
                const percentage = getPercentage(total, (d as any).data.frequency);
                const pos = arc.centroid(d as any);
                if (percentage >= 6) {
                    return "translate(" + pos + ")";
                }
                return "translate(" + [radius * Math.sin(midAngle(d)), -(radius + 5) * Math.cos(midAngle(d))] + ")";
            })
            .style("fill", function (d) {
                const percentage = getPercentage(total, (d as any).data.frequency);
                if (percentage < 6) {
                    return "darkgrey";
                }
                return "white";
            })
            .attr("text-anchor", "middle")
            .style("font-size", `${this.props.radius / 9}px`)
            .attr("class", function (d) {
                const percentage = getPercentage(total, (d as any).data.frequency);
                if (percentage < 6) {
                    return "labels--hidden labels--black";
                }
                return "";
            })
            .attr("data-id", function (d: any) {
                return "labels" + d.data.label;
            })
            .on("mouseover", this.mouseOver)
            .on("mouseout", this.mouseOut);

        if (this.props.selectedItem || this.state.selectedItem) {
            const selectedItem = this.props.selectedItem ? this.props.selectedItem : this.state.selectedItem;

            pieSvg.selectAll(`[data-id=${selectedItem}]`)
                .style("fill", EquipmentPieChart.DEFAULT_COLOR);

            pieSvg.selectAll(`[data-id=labels${selectedItem}]`)
                .classed("labels--visible", true)
                .classed("labels--hidden", false);
        }
        return div.toReact();
    }

    public mouseOver(event, d) {
        this.props.onPathHover(d.data.id);

        const pieRow = document.querySelectorAll(`[data-equipment-id='${d.data.id}']`)[0];
        this.setState({
            selectedItem: `${d.data.id}`,
        });

        document.onclick = () => {
            if (this.state.selectedItem) {
                const pieRow = (document.querySelectorAll(
                    `[data-equipment-id='${this.state.selectedItem}']`)[0] as HTMLDivElement);
                pieRow.click();
            }
        };

        pieRow.classList.add("active");
    }

    public mouseOut(event, d) {
        this.props.onPathHover(null);

        const pieRow = document.querySelectorAll(`[data-equipment-id='${d.data.id}']`)[0];
        this.setState({
            selectedItem: undefined,
        });

        pieRow.classList.remove("active");
    }
}
